/**
 * AdminBillingPage - Subscription overview for super admins
 * Lists all company subscriptions, MRR summary, manual grant/revoke.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import adminService from '../../services/adminService';
import AdminLayout from './AdminLayout';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Content = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const SummaryCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.text};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const Tr = styled.tr`
  transition: background 0.15s;
  &:hover { background: ${(p) => p.theme.colors.bgTertiary}; }
  &:last-child td { border-bottom: none; }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  ${(p) => {
    switch (p.$status) {
      case 'active': return `background: rgba(16,185,129,0.15); color: #10B981; border: 1px solid #10B981;`;
      case 'past_due': return `background: rgba(245,158,11,0.15); color: #F59E0B; border: 1px solid #F59E0B;`;
      case 'canceled': return `background: rgba(239,68,68,0.15); color: #EF4444; border: 1px solid #EF4444;`;
      default: return `background: ${p.theme.colors.bgTertiary}; color: ${p.theme.colors.textSecondary}; border: 1px solid ${p.theme.colors.border};`;
    }
  }}
`;

const ActionButton = styled.button`
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${(p) => p.theme.fonts.main};
  transition: all 0.15s;

  ${(p) => p.$grant
    ? `
      background: rgba(16,185,129,0.1);
      border: 1px solid #10B981;
      color: #10B981;
      &:hover { background: rgba(16,185,129,0.2); }
    `
    : `
      background: rgba(239,68,68,0.1);
      border: 1px solid #EF4444;
      color: #EF4444;
      &:hover { background: rgba(239,68,68,0.2); }
    `}

  &:disabled { opacity: 0.4; cursor: default; }
`;

function AdminBillingPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getSubscriptions();
      setData(result);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGrant = async (companyId) => {
    if (!window.confirm(t('このサブスクリプションを有効にしますか？', 'Activate this subscription?'))) return;
    setActionLoading(companyId);
    try {
      await adminService.grantSubscription(companyId);
      await fetchData();
    } catch (err) {
      alert(err.message || 'Error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (companyId) => {
    if (!window.confirm(t('このサブスクリプションを取り消しますか？', 'Revoke this subscription?'))) return;
    setActionLoading(companyId);
    try {
      await adminService.revokeSubscription(companyId);
      await fetchData();
    } catch (err) {
      alert(err.message || 'Error');
    } finally {
      setActionLoading(null);
    }
  };

  if (error) {
    return (
      <AdminLayout active="billing">
        <Content style={{ textAlign: 'center', padding: 64 }}>
          <p style={{ color: '#EF4444' }}>{error}</p>
        </Content>
      </AdminLayout>
    );
  }

  const summary = data?.summary || {};
  const subscriptions = data?.subscriptions || [];

  return (
    <AdminLayout active="billing">
      <Content>
        <SummaryGrid>
          <SummaryCard>
            <SummaryLabel>{t('合計MRR', 'Total MRR')}</SummaryLabel>
            <SummaryValue>
              {loading ? '--' : `¥${(summary.total_mrr || 0).toLocaleString()}`}
            </SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>{t('有料契約数', 'Active Subscriptions')}</SummaryLabel>
            <SummaryValue>{loading ? '--' : summary.active_count || 0}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>{t('全サブスクリプション', 'Total Subscriptions')}</SummaryLabel>
            <SummaryValue>{loading ? '--' : summary.total_count || 0}</SummaryValue>
          </SummaryCard>
        </SummaryGrid>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 64, color: '#8888a8' }}>
            {t('読み込み中...', 'Loading...')}
          </div>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('企業名', 'Company')}</Th>
                  <Th>{t('ステータス', 'Status')}</Th>
                  <Th>{t('メンバー', 'Members')}</Th>
                  <Th>{t('有料席', 'Billable')}</Th>
                  <Th>MRR</Th>
                  <Th>{t('更新日', 'Period End')}</Th>
                  <Th>Stripe</Th>
                  <Th>{t('操作', 'Actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length === 0 ? (
                  <tr>
                    <Td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#8888a8' }}>
                      {t('サブスクリプションがありません', 'No subscriptions')}
                    </Td>
                  </tr>
                ) : (
                  subscriptions.map((s) => (
                    <Tr key={s.id}>
                      <Td>
                        <div style={{ fontWeight: 600 }}>{s.company_name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8888a8' }}>{s.company_slug}</div>
                      </Td>
                      <Td>
                        <StatusBadge $status={s.status}>{s.status}</StatusBadge>
                        {s.is_admin_grant && (
                          <span style={{ marginLeft: 6, fontSize: '0.68rem', color: '#EC4899', fontWeight: 600 }}>
                            GRANT
                          </span>
                        )}
                      </Td>
                      <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        {s.active_members}
                      </Td>
                      <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        {s.billable_seats}
                      </Td>
                      <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        ¥{(s.mrr || 0).toLocaleString()}
                      </Td>
                      <Td style={{ fontSize: '0.82rem', color: '#8888a8' }}>
                        {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '--'}
                      </Td>
                      <Td style={{ fontSize: '0.72rem', color: '#8888a8', fontFamily: "'JetBrains Mono', monospace" }}>
                        {s.stripe_subscription_id ? s.stripe_subscription_id.slice(0, 16) + '...' : '--'}
                      </Td>
                      <Td>
                        {s.status === 'active' ? (
                          <ActionButton
                            onClick={() => handleRevoke(s.company_id)}
                            disabled={actionLoading === s.company_id}
                          >
                            {t('取消', 'Revoke')}
                          </ActionButton>
                        ) : (
                          <ActionButton
                            $grant
                            onClick={() => handleGrant(s.company_id)}
                            disabled={actionLoading === s.company_id}
                          >
                            {t('付与', 'Grant')}
                          </ActionButton>
                        )}
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Content>
    </AdminLayout>
  );
}

export default AdminBillingPage;
